import { useState, useEffect } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@werkstatt/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@werkstatt/ui';
import { Shield, UserIcon } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { UserPermissionsEditor } from '../../components/UserPermissionsEditor';
import api from '../../lib/api';

interface UserItem {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  membership_role: string;
  is_active: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: UserItem | null;
}

export function UserDialog({ open, onOpenChange, user }: Props) {
  const isEdit = !!user;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('user');

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setFirstName(user.first_name ?? '');
      setLastName(user.last_name ?? '');
      setPhone(user.phone ?? '');
      setRole(user.membership_role);
    } else {
      setEmail('');
      setFirstName('');
      setLastName('');
      setPhone('');
      setRole('user');
    }
  }, [user, open]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/users', {
        email,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        phone: phone || undefined,
        membership_role: role,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onOpenChange(false);
      toast({ title: 'Benutzer angelegt', description: 'Einladungslink wurde erstellt.' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Benutzer konnte nicht angelegt werden',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      await api.put(`/users/${user!.id}`, {
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        phone: phone || undefined,
        membership_role: role,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onOpenChange(false);
      toast({ title: 'Benutzer gespeichert' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Fehler beim Speichern',
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSave = () => {
    if (isEdit) updateMutation.mutate();
    else createMutation.mutate();
  };

  const profileForm = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Vorname</Label>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Max" />
        </div>
        <div className="space-y-1.5">
          <Label>Nachname</Label>
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Mustermann" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>E-Mail {!isEdit && <span className="text-destructive">*</span>}</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="max@beispiel.de"
          disabled={isEdit}
        />
        {isEdit && (
          <p className="text-xs text-muted-foreground">E-Mail kann hier nicht geändert werden</p>
        )}
      </div>
      <div className="space-y-1.5">
        <Label>Telefon</Label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+49 123 456789" />
      </div>
      <div className="space-y-1.5">
        <Label>Rolle</Label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="w-full border rounded-md px-3 py-2 text-sm bg-background"
        >
          <option value="user">Benutzer</option>
          <option value="admin">Admin</option>
        </select>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? `Benutzer: ${[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}` : 'Neuer Benutzer'}
          </DialogTitle>
        </DialogHeader>

        {isEdit ? (
          <Tabs defaultValue="profile">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="profile">
                <UserIcon className="h-4 w-4 mr-2" />
                Profil
              </TabsTrigger>
              <TabsTrigger value="permissions">
                <Shield className="h-4 w-4 mr-2" />
                Berechtigungen
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              {profileForm}
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                <Button onClick={handleSave} disabled={isPending}>
                  {isPending ? 'Speichern...' : 'Speichern'}
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="permissions">
              <UserPermissionsEditor userId={user.id} isAdmin={user.membership_role === 'admin'} />
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        ) : (
          <>
            {profileForm}
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
              <Button onClick={handleSave} disabled={isPending || !email}>
                {isPending ? 'Anlegen...' : 'Benutzer anlegen'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
